import { useState } from "react";
import {
  Check,
  ExternalLink,
  Link2,
  Loader2,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Share2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Button,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@sportscanner/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import PhoneInput from "@/components/booking/phone-input";
import { apiGet, apiPost } from "@/lib/api";
import { type MeClub, useMe } from "@/lib/me";

const SITE_URL = import.meta.env.VITE_SITE_URL || "http://localhost:3000";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  clubId: string;
  className?: string;
}

// Open a deep link / app handler. An anchor click (rather than window.open) lets
// viber:// and sms: hand off to the native app without leaving a blank tab
// behind in most browsers; for wa.me (https) it opens WhatsApp Web/Desktop.
function openExternal(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function ShareClubButton({ clubId, className }: Props) {
  const { t, i18n } = useTranslation();
  const { club: meClub } = useMe();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  // STAFF can't hit GET /api/admin/clubs/:clubId (403). When the prop matches
  // the user's own club we already have name + slug from /api/admin/me, so skip
  // the fetch — only SUPERADMIN browsing a different club still needs it.
  const useMeClub = meClub?.id === clubId;
  const { data: fetchedClub } = useQuery({
    queryKey: ["admin", "clubs", clubId],
    queryFn: async () => {
      const data = await apiGet<{ club: MeClub }>(`/api/admin/clubs/${clubId}`);
      return data.club;
    },
    enabled: open && !useMeClub,
  });

  const club = useMeClub ? meClub : fetchedClub;
  const slug = club?.slug;
  const clubName = club?.name ?? "";
  const url = slug ? `${SITE_URL.replace(/\/$/, "")}/${slug}` : null;
  const message = t("clubUrl.message", { club: clubName });
  const shareText = url ? `${message} ${url}` : "";

  const lang = i18n.language?.startsWith("en")
    ? "en"
    : i18n.language?.startsWith("ru")
      ? "ru"
      : "sr";

  async function handleCopy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("clubUrl.copied"));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("clubUrl.copyError"));
    }
  }

  async function handleNativeShare() {
    if (!url) return;
    try {
      await navigator.share({ title: clubName, text: message, url });
    } catch (err) {
      // User dismissed the sheet → do nothing; anything else → copy fallback.
      if ((err as DOMException)?.name === "AbortError") return;
      void handleCopy();
    }
  }

  function shareWhatsApp() {
    if (!url) return;
    const digits = phone.replace(/\D/g, "");
    const base = digits ? `https://wa.me/${digits}` : "https://wa.me/";
    openExternal(`${base}?text=${encodeURIComponent(shareText)}`);
  }

  function shareViber() {
    if (!url) return;
    // Viber's deep link can't reliably target a number; `forward` opens Viber
    // with the text pre-filled and lets the admin pick the contact.
    openExternal(`viber://forward?text=${encodeURIComponent(shareText)}`);
  }

  function shareSms() {
    if (!url) return;
    const num = phone.replace(/\s/g, "");
    openExternal(`sms:${num}?body=${encodeURIComponent(shareText)}`);
  }

  async function sendEmail() {
    if (!url) return;
    if (!EMAIL_RE.test(email.trim())) {
      toast.error(t("clubUrl.emailInvalid"));
      return;
    }
    setSending(true);
    try {
      await apiPost(`/api/admin/clubs/${clubId}/share-link`, {
        email: email.trim(),
        language: lang,
      });
      toast.success(t("clubUrl.emailSent", { email: email.trim() }));
      setEmail("");
    } catch {
      toast.error(t("clubUrl.emailError"));
    } finally {
      setSending(false);
    }
  }

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className={className ?? "rounded-full"}
      >
        <Share2 className="w-4 h-4 mr-2" />
        {t("clubUrl.share")}
      </Button>

      <ResponsiveDialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) {
            setPhone("");
            setEmail("");
          }
        }}
      >
        <ResponsiveDialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("clubUrl.dialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("clubUrl.dialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* The link itself — copy / open */}
            <div className="space-y-1.5">
              <Label className="text-xs text-neutral-500">
                {t("clubUrl.linkLabel")}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={url ?? ""}
                  onFocusCapture={(e) => e.currentTarget.select()}
                  className="flex-1 bg-neutral-50 text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  disabled={!url}
                  aria-label={t("clubUrl.copyUrl")}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Link2 className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  asChild
                  disabled={!url}
                  aria-label={t("clubUrl.openUrl")}
                  className="shrink-0"
                >
                  <a
                    href={url ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Native share sheet — best UX on a phone/tablet at the desk */}
            {canNativeShare && (
              <Button
                type="button"
                onClick={handleNativeShare}
                disabled={!url}
                className="w-full rounded-lg bg-primary-600 hover:bg-primary-700 text-white"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t("clubUrl.nativeShare")}
              </Button>
            )}

            {/* Send directly via a messaging app */}
            <div className="space-y-3 border-t border-neutral-100 pt-4">
              <p className="text-sm font-medium text-neutral-700">
                {t("clubUrl.sendDirectly")}
              </p>
              <div className="space-y-1.5">
                <Label
                  htmlFor="share-phone"
                  className="text-xs text-neutral-500"
                >
                  {t("clubUrl.phoneLabel")}
                </Label>
                <PhoneInput
                  id="share-phone"
                  value={phone}
                  onChange={setPhone}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  onClick={shareWhatsApp}
                  disabled={!url}
                  className="flex-col gap-1 h-auto py-2.5 sm:flex-row sm:gap-2 sm:h-9 sm:py-2 rounded-lg bg-[#25D366] hover:bg-[#1ebe5b] text-white"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">
                    {t("clubUrl.whatsapp")}
                  </span>
                </Button>
                <Button
                  type="button"
                  onClick={shareViber}
                  disabled={!url}
                  className="flex-col gap-1 h-auto py-2.5 sm:flex-row sm:gap-2 sm:h-9 sm:py-2 rounded-lg bg-[#7360F2] hover:bg-[#5e4fd0] text-white"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">
                    {t("clubUrl.viber")}
                  </span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={shareSms}
                  disabled={!url}
                  className="flex-col gap-1 h-auto py-2.5 sm:flex-row sm:gap-2 sm:h-9 sm:py-2 rounded-lg"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">{t("clubUrl.sms")}</span>
                </Button>
              </div>
            </div>

            {/* Send by email (delivered server-side via Resend) */}
            <div className="space-y-1.5 border-t border-neutral-100 pt-4">
              <Label htmlFor="share-email" className="text-xs text-neutral-500">
                {t("clubUrl.emailLabel")}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="share-email"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void sendEmail();
                    }
                  }}
                  placeholder={t("clubUrl.emailPlaceholder")}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={() => void sendEmail()}
                  disabled={!url || sending || !email.trim()}
                  className="rounded-lg bg-primary-600 hover:bg-primary-700 text-white shrink-0"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-1.5" />
                      {t("clubUrl.sendEmail")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
}
